/**
 * Sentry Error Monitoring Configuration
 *
 * Provides production-grade error tracking with context for:
 * - Tenant ID (multi-tenant architecture)
 * - User ID (authenticated users)
 * - Widget Session ID (public widget sessions)
 */

import * as Sentry from '@sentry/react';

// Check if we're in production
const isProd = import.meta.env.PROD;
const isVercel = !!import.meta.env.VITE_VERCEL;

/**
 * Initialize Sentry for the React application
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize if DSN is configured
  if (!dsn) {
    if (isProd) {
      console.warn('[Sentry] VITE_SENTRY_DSN not configured - error tracking disabled');
    }
    return;
  }

  Sentry.init({
    dsn,

    // Environment detection
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ||
      (isProd ? 'production' : 'development'),

    // Release tracking (uses Vercel git commit SHA or falls back to 'dev')
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ||
      import.meta.env.VITE_SENTRY_RELEASE ||
      'dev',

    // Performance monitoring
    tracesSampleRate: isProd ? 0.1 : 1.0, // 10% in prod, 100% in dev
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.vercel\.app/,
      /^https:\/\/renovation-ai/,
    ],

    // Session replay (disabled for performance, enable if needed)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProd ? 0.1 : 0,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Filter out noise
    ignoreErrors: [
      // Common browser extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Network errors users can't do anything about
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      // Clerk auth errors (handled gracefully)
      'ClerkRuntimeError',
      // User cancelled operations
      'AbortError',
    ],

    // Don't send in development unless explicitly enabled
    enabled: isProd || import.meta.env.VITE_SENTRY_ENABLED === 'true',

    // Attach stack traces to all messages
    attachStacktrace: true,

    // Before sending, add additional context
    beforeSend(event, hint) {
      // Filter console.log breadcrumbs in production
      if (isProd && event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.filter(
          (breadcrumb) => breadcrumb.category !== 'console' ||
            breadcrumb.level === 'error' ||
            breadcrumb.level === 'warning'
        );
      }

      return event;
    },
  });

  // Log initialization
  if (!isProd) {
    console.log('[Sentry] Initialized with DSN:', dsn.substring(0, 30) + '...');
  }
}

/**
 * Set user context for authenticated users
 */
export function setSentryUser(user: {
  id: string;
  email?: string | null;
  tenantId?: string | null;
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email || undefined,
  });

  if (user.tenantId) {
    Sentry.setTag('tenant_id', user.tenantId);
  }
}

/**
 * Clear user context on sign out
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
  Sentry.setTag('tenant_id', undefined);
  Sentry.setTag('session_id', undefined);
}

/**
 * Set widget session context (for public widget users)
 */
export function setSentryWidgetSession(sessionId: string, tenantId: string): void {
  Sentry.setTag('session_id', sessionId);
  Sentry.setTag('tenant_id', tenantId);
  Sentry.setTag('context', 'widget');
}

/**
 * Capture an exception with optional context
 */
export function captureError(
  error: unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  }
): string {
  return Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
    level: context?.level,
  });
}

/**
 * Capture a message (non-error event)
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info'
): string {
  return Sentry.captureMessage(message, level);
}

/**
 * Add a breadcrumb for debugging context
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Create a performance transaction span
 */
export function startSpan<T>(
  name: string,
  op: string,
  callback: () => T
): T {
  return Sentry.startSpan({ name, op }, callback);
}

// Re-export Sentry for direct access when needed
export { Sentry };
